import React, { useEffect, ReactNode } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: any;
  children: ReactNode;
  primaryLabel?: string;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  saving?: boolean;
  error?: string | null;
}

const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: Icon,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  saving,
  error,
}) => {
  const { isDark } = useTheme();

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      <div
        className={`relative w-full max-w-md rounded-2xl shadow-xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
        role="dialog"
        aria-label={title}
      >
        <div
          className={`px-5 py-4 border-b flex items-center justify-between flex-shrink-0 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center gap-3">
            {Icon && (
              <div
                className={`p-2 rounded-xl ${
                  isDark ? 'bg-sminy-primary/15 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>
            )}
            <div>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {title}
              </h2>
              {subtitle && (
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            title="Fechar (Esc)"
            className={`p-2 rounded-xl transition-colors ${
              isDark
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {children}

          {error && (
            <div
              className={`flex items-start gap-2 p-3 rounded-xl text-sm ${
                isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-600'
              }`}
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div
          className={`px-5 py-4 border-t flex items-center justify-end gap-2 flex-shrink-0 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <button
            onClick={onClose}
            disabled={saving}
            className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
              isDark
                ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Cancelar
          </button>
          {onPrimary && (
            <button
              onClick={onPrimary}
              disabled={primaryDisabled || saving}
              className="rounded-xl bg-sminy-primary hover:bg-sminy-primary/90 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium text-white transition-colors flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {primaryLabel || 'Salvar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaseModal;
